import { memo, useCallback, useMemo, useState } from 'react';
import { RichTextInputGroup } from '@/components/admin/input-groups/rich-text-input-group/RichTextInputGroup';
import { Button } from '@/components/admin/button/Button';
import { ConfirmationModal } from '@/components/admin/confirmation-modal/ConfirmationModal';
import { ReactComponent as PlusIcon } from '@/assets/icons/plus.svg';
import { COMMON_TEXT_ADMIN } from '@/const/admin/common';
import { HIPPOTHERAPY_PAGE_CHAR_LIMITS, HIPPOTHERAPY_PAGE_TEXT } from '@/const/admin/hippotherapy-page';
import { HIPPOTHERAPY_PAGE_VALIDATION_FUNCTIONS } from '@/validation/admin/hippotherapy-page-schema/HippotherapyPageSchema';
import { useValidatedRichTextField } from '@/hooks/admin/use-validated-rich-text-field/useValidatedRichTextField';
import {
    HippotherapyScientificReference,
    HippotherapyScientificReferencesSectionContent,
} from '@/types/admin/hippotherapy-page';
import { SectionTranslationHeader } from '../../section-translation-header/SectionTranslationHeader';
import { ScientificReferenceCard } from './scientific-reference-card/ScientificReferenceCard';
import './ScientificReferencesSection.scss';
import { LocalizationLanguage, EntityLocalization } from '@/types/common/language';

export interface ScientificReferencesSectionProps {
    value: HippotherapyScientificReferencesSectionContent;
    onChange: (value: HippotherapyScientificReferencesSectionContent) => void;
    disabled?: boolean;
    onTranslateClick?: () => void;
    isTranslateDisabled?: boolean;
    onTranslateGeneralClick?: () => void;
    isTranslateGeneralDisabled?: boolean;
    onTranslateReferenceClick?: (localId: string) => void;
    isTranslateReferenceDisabled?: (localId: string) => boolean;
    translationLanguages?: LocalizationLanguage[];
    localizations?: EntityLocalization[];
}

const getExpandKey = (reference: HippotherapyScientificReference) =>
    reference.id !== null ? `id-${reference.id}` : `local-${reference.localId}`;

const validateReferenceName = (text: string) =>
    HIPPOTHERAPY_PAGE_VALIDATION_FUNCTIONS.validateText(text.trim(), HIPPOTHERAPY_PAGE_TEXT.MIN_REFERENCE_NAME_LENGTH);

const validateReferenceUrl = (text: string) =>
    HIPPOTHERAPY_PAGE_VALIDATION_FUNCTIONS.validateText(text.trim(), HIPPOTHERAPY_PAGE_TEXT.MIN_REFERENCE_URL_LENGTH);

const ScientificReferencesSectionComponent = ({
    value,
    onChange,
    disabled,
    onTranslateClick,
    isTranslateDisabled,
    onTranslateGeneralClick,
    isTranslateGeneralDisabled,
    onTranslateReferenceClick,
    isTranslateReferenceDisabled,
    translationLanguages,
    localizations,
}: ScientificReferencesSectionProps) => {
    const title = useValidatedRichTextField({
        value: value.title,
        onChange: (title) => onChange({ ...value, title }),
        minLength: HIPPOTHERAPY_PAGE_TEXT.MIN_TITLE_LENGTH,
    });

    const description = useValidatedRichTextField({
        value: value.description,
        onChange: (description) => onChange({ ...value, description }),
    });

    const [nameErrors, setNameErrors] = useState<Record<string, string | undefined>>({});
    const [urlErrors, setUrlErrors] = useState<Record<string, string | undefined>>({});
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [newReferenceLocalId, setNewReferenceLocalId] = useState<string | null>(null);
    const [referenceToDeleteLocalId, setReferenceToDeleteLocalId] = useState<string | null>(null);

    const areAllReferencesValid = useMemo(
        () =>
            value.scientificReferences.every(
                (reference) => !validateReferenceName(reference.name) && !validateReferenceUrl(reference.url),
            ),
        [value.scientificReferences],
    );

    const handleToggleExpand = useCallback(
        (localId: string) => {
            const reference = value.scientificReferences.find((item) => item.localId === localId);
            if (!reference) return;

            const key = getExpandKey(reference);

            setExpandedIds((prev) => {
                const next = new Set(prev);
                if (next.has(key)) {
                    next.delete(key);
                } else {
                    next.add(key);
                }
                return next;
            });
        },
        [value.scientificReferences],
    );

    const handleAddReference = useCallback(() => {
        const newReference: HippotherapyScientificReference = {
            localId: crypto.randomUUID(),
            id: null,
            name: '',
            url: '',
        };

        onChange({
            ...value,
            scientificReferences: [...value.scientificReferences, newReference],
        });

        setExpandedIds((prev) => new Set(prev).add(getExpandKey(newReference)));
        setNewReferenceLocalId(newReference.localId);
    }, [onChange, value]);

    const handleDeleteRequest = useCallback((localId: string) => {
        setReferenceToDeleteLocalId(localId);
    }, []);

    const handleDeleteCancel = useCallback(() => {
        setReferenceToDeleteLocalId(null);
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (!referenceToDeleteLocalId) return;

        const reference = value.scientificReferences.find((item) => item.localId === referenceToDeleteLocalId);

        onChange({
            ...value,
            scientificReferences: value.scientificReferences.filter(
                (item) => item.localId !== referenceToDeleteLocalId,
            ),
        });

        if (reference) {
            const key = getExpandKey(reference);
            setExpandedIds((prev) => {
                if (!prev.has(key)) return prev;
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }

        setReferenceToDeleteLocalId(null);
    }, [onChange, value, referenceToDeleteLocalId]);

    const handleNameChange = (localId: string, name: string) => {
        const scientificReferences = value.scientificReferences.map((reference) =>
            reference.localId === localId ? { ...reference, name } : reference,
        );
        onChange({ ...value, scientificReferences });

        if (nameErrors[localId] !== undefined) {
            setNameErrors((prev) => ({ ...prev, [localId]: validateReferenceName(name) }));
        }
    };

    const handleUrlChange = (localId: string, url: string) => {
        const scientificReferences = value.scientificReferences.map((reference) =>
            reference.localId === localId ? { ...reference, url } : reference,
        );
        onChange({ ...value, scientificReferences });

        if (urlErrors[localId] !== undefined) {
            setUrlErrors((prev) => ({ ...prev, [localId]: validateReferenceUrl(url) }));
        }
    };

    const handleNameBlur = (localId: string) => {
        const reference = value.scientificReferences.find((item) => item.localId === localId);
        setNameErrors((prev) => ({ ...prev, [localId]: validateReferenceName(reference?.name ?? '') }));
    };

    const handleUrlBlur = (localId: string) => {
        const reference = value.scientificReferences.find((item) => item.localId === localId);
        setUrlErrors((prev) => ({ ...prev, [localId]: validateReferenceUrl(reference?.url ?? '') }));
    };

    const generalTranslateClick = onTranslateGeneralClick ?? onTranslateClick;
    const generalTranslateDisabled = isTranslateGeneralDisabled ?? isTranslateDisabled;

    return (
        <div className="scientific-references-section">
            <div className="scientific-references-section-summary">
                <SectionTranslationHeader
                    onTranslateClick={generalTranslateClick}
                    isTranslateDisabled={generalTranslateDisabled}
                    languages={translationLanguages}
                    localizations={localizations}
                />

                <RichTextInputGroup
                    label={COMMON_TEXT_ADMIN.TYPE.TITLE}
                    isRequired
                    id="scientific-references-title"
                    name="scientific-references-title"
                    value={value.title}
                    onChange={title.handleChange}
                    onBlur={title.handleBlur}
                    maxLength={HIPPOTHERAPY_PAGE_CHAR_LIMITS.RESEARCH_TITLE}
                    error={title.error}
                    disabled={disabled}
                />
                <RichTextInputGroup
                    label={COMMON_TEXT_ADMIN.TYPE.DESCRIPTION}
                    isRequired
                    id="scientific-references-description"
                    name="scientific-references-description"
                    value={value.description}
                    onChange={description.handleChange}
                    onBlur={description.handleBlur}
                    maxLength={HIPPOTHERAPY_PAGE_CHAR_LIMITS.RESEARCH_DESCRIPTION}
                    error={description.error}
                    disabled={disabled}
                />
            </div>
            <div className="scientific-references-section-list">
                {value.scientificReferences.map((reference) => (
                    <ScientificReferenceCard
                        key={reference.localId}
                        localId={reference.localId}
                        name={reference.name}
                        url={reference.url}
                        isExpanded={expandedIds.has(getExpandKey(reference))}
                        autoFocus={reference.localId === newReferenceLocalId}
                        canDelete={value.scientificReferences.length > 1}
                        nameError={nameErrors[reference.localId]}
                        urlError={urlErrors[reference.localId]}
                        disabled={disabled}
                        onToggleExpand={handleToggleExpand}
                        onDelete={handleDeleteRequest}
                        onNameChange={handleNameChange}
                        onUrlChange={handleUrlChange}
                        onNameBlur={handleNameBlur}
                        onUrlBlur={handleUrlBlur}
                        onTranslateClick={
                            onTranslateReferenceClick ? () => onTranslateReferenceClick(reference.localId) : undefined
                        }
                        isTranslateDisabled={
                            isTranslateReferenceDisabled ? isTranslateReferenceDisabled(reference.localId) : false
                        }
                    />
                ))}
                <Button
                    buttonStyle="primary"
                    type="button"
                    className="scientific-references-section-add-button"
                    onClick={handleAddReference}
                    disabled={disabled || !areAllReferencesValid}
                >
                    {HIPPOTHERAPY_PAGE_TEXT.BUTTON.ADD_REFERENCE}
                    <PlusIcon />
                </Button>
            </div>
            <ConfirmationModal
                isOpen={referenceToDeleteLocalId !== null}
                title={HIPPOTHERAPY_PAGE_TEXT.CONFIRMATION.DELETE_REFERENCE}
                onClose={handleDeleteCancel}
                onCancel={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
            />
        </div>
    );
};

export const ScientificReferencesSection = memo(ScientificReferencesSectionComponent);
